// src/pages/packages/PackagesPage.jsx
import "./PackagesPage.scss";

import { Col, Row } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { faAdd, faClipboardList } from "@fortawesome/free-solid-svg-icons";

import AppLoading from "../../../../commons/AppLoading";
import ConfirmModal from "../../../../commons/modal/ConfirmModal";
import EmptyState from "../../../../commons/EmptyState";
import FloatingButton from "../../../../commons/buttons/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../../commons/HallAppToast";
import MenuTitle from "../../../../commons/menu/MenuTitle";
import ModalPackageConfiguration from "./ModalPackageConfiguration";
import ModalSelectCustomers from "../../../../commons/modal/ModalSelectCustomers";
import PackageAPI from "../api/PackageAPI";
import PackageCard from "./PackageCard";
import PackageSubscriptionAPI from "../api/PackageSubscriptionAPI";
import { useNavigate } from "react-router-dom";

const packageAPI = new PackageAPI();
const packageSubscriptionAPI = new PackageSubscriptionAPI();

export default function PackagesPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [showModalSelectCustomers, setShowModalSelectCustomers] = useState(false);
  const [selectedPackageForCustomers, setSelectedPackageForCustomers] = useState(null);

  const navigate = useNavigate();

  const handleViewCustomers = (pkg) => {
    navigate(`/pacotes/${pkg?.id}/clientes`);
  };

  const load = async () => {
    try {
      setIsLoading(true);

      const resp = await packageAPI.findAll();
      const data = resp?.data || resp;
      const list = data?.items || data;

      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setItems([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = (id) => {
    setEditingId(id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const toggleStatus = async (p) => {
    try {
      setIsLoading(true);

      const next = p?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      await packageAPI.updateStatus(p.id, next);

      HallAppToast.successWithMessage("Status atualizado com sucesso.");
      await load();
    } catch (e) {
      HallAppToast.genericError();
      setIsLoading(false);
    }
  };

  const openDeleteModal = (p) => {
    setCurrentItem(p);
    setShowModalDelete(true);
  };

  const closeDeleteModal = () => {
    if (isLoadingModalDelete) return;
    setShowModalDelete(false);
    setCurrentItem(null);
  };

  const executeDeletion = async () => {
    if (!currentItem?.id) return;

    setIsLoadingModalDelete(true);

    try {
      await packageAPI.delete(currentItem.id);

      HallAppToast.success("Pacote excluído com sucesso.");
      setShowModalDelete(false);
      setCurrentItem(null);

      await load();
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  };

  const handleAddCustomers = (pkg) => {
    setSelectedPackageForCustomers(pkg);
    setShowModalSelectCustomers(true);
  };

  const handleConfirmCustomers = async (selectedCustomers) => {
    try {
      const packageId = selectedPackageForCustomers?.id;

      const customerIds = selectedCustomers.map((c) => c.id);

      setIsLoading(true);
      const data = {
        packageId,
        customerIds
      }

      await packageSubscriptionAPI.bulkCreate(data);

      HallAppToast.success();

      setShowModalSelectCustomers(false);
      setSelectedPackageForCustomers(null);
    } catch (e) {
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="container packages-page">
        <MenuTitle
          title="Pacotes"
          subtitle="Crie e gerencie pacotes de serviços para vender com valor fechado."
          showArrowInfo={false}
        />

        {isLoading ? (
          <AppLoading />
        ) : (
          <Row className="g-3" style={{ margin: 0, width: "100%" }}>
            {items.length === 0 ? (
              <EmptyState
                title="Nenhum pacote criado"
                description="Crie pacotes para aumentar ticket médio e fidelizar clientes."
                icon={<FontAwesomeIcon icon={faClipboardList} />}
                actionLabel="Criar meu primeiro pacote"
                onAction={openCreate}
              />
            ) : (
              items.map((p) => (
                <Col xs={12} sm={6} md={4} lg={4} key={p.id} className="mb-3">
                  <PackageCard
                    pkg={p}
                    onEdit={() => openEdit(p.id)}
                    onToggleStatus={() => toggleStatus(p)}
                    onDelete={() => openDeleteModal(p)}
                    onViewCustomers={() => handleViewCustomers(p)}
                    onAddCustomers={() => handleAddCustomers(p)}
                  />
                </Col>
              ))
            )}

            <div style={{ height: "calc(50px + 72px)" }} />
          </Row>
        )}
      </div>

      <FloatingButton icon={faAdd} onClick={openCreate} />

      {showModal && (
        <ModalPackageConfiguration
          show={showModal}
          closeShow={closeModal}
          packageId={editingId}
          onSuccess={() => load()}
        />
      )}

      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir pacote"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={closeDeleteModal}
        >
          Deseja realmente remover o pacote{" "}
          <strong>{currentItem?.name || ""}</strong>?
        </ConfirmModal>
      )}

      {showModalSelectCustomers && (
        <ModalSelectCustomers
          show={showModalSelectCustomers}
          closeShow={() => {
            setShowModalSelectCustomers(false);
            setSelectedPackageForCustomers(null);
          }}
          multiple={true}
          onConfirm={handleConfirmCustomers}
          title={`Adicionar Clientes ao Pacote: ${selectedPackageForCustomers?.name}`}
          confirmLabel="Adicionar ao Pacote"
        />
      )}
    </>
  );
}